import axios from 'axios';
import { extractDomain, formatCompanyName } from './websiteAnalyzer';

// Rate limiting configuration
const RATE_LIMIT = {
  maxConcurrent: 2, // Lower limit for opener generation to prevent overload
  delayBetweenRequests: 1500, // 1.5 second delay between requests
  retryAttempts: 3,
  retryDelay: 2000 // 2 seconds initial retry delay
};

// Queue to manage concurrent requests
let activeOpenerRequests = 0;
let openerRequestQueue = [];

// Rate limiting helper functions
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const executeOpenerWithRateLimit = async (apiCall) => {
  return new Promise((resolve, reject) => {
    const execute = async () => {
      if (activeOpenerRequests >= RATE_LIMIT.maxConcurrent) {
        // Add to queue if at max concurrent requests
        openerRequestQueue.push(execute);
        return;
      }
      
      activeOpenerRequests++;
      
      try {
        const result = await apiCall();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        activeOpenerRequests--;
        
        // Process next request in queue after delay
        if (openerRequestQueue.length > 0) {
          setTimeout(() => {
            const nextRequest = openerRequestQueue.shift();
            nextRequest();
          }, RATE_LIMIT.delayBetweenRequests);
        }
      }
    };
    
    execute();
  });
};

const executeOpenerWithRetry = async (apiCall, retryCount = 0) => {
  try {
    return await executeOpenerWithRateLimit(apiCall);
  } catch (error) {
    if (retryCount < RATE_LIMIT.retryAttempts && 
        (error.response?.status === 500 || error.response?.status === 429 || error.code === 'ECONNRESET')) {
      console.log(`Opener API call failed, retrying in ${RATE_LIMIT.retryDelay * (retryCount + 1)}ms... (attempt ${retryCount + 1}/${RATE_LIMIT.retryAttempts})`);
      await delay(RATE_LIMIT.retryDelay * (retryCount + 1)); // Exponential backoff
      return executeOpenerWithRetry(apiCall, retryCount + 1);
    }
    throw error;
  }
};

// Wrapper for makeAIRequest with rate limiting
const makeAIRequest = async (config, prompt) => {
  const apiCall = async () => {
    return await makeAIRequestInternal(config, prompt, 0);
  };
  
  try {
    return await executeOpenerWithRetry(apiCall);
  } catch (error) {
    console.error('AI request failed after retries:', error.message);
    return { error: error.message || 'AI request failed after multiple attempts' };
  }
};

/**
 * Calls the service opportunities API to get Y-component opportunities
 * @param {string} websiteUrl - The website URL to analyze
 * @param {string} serviceString - The service type with focus area
 * @returns {Promise<Array>} Array of service-specific opportunities
 */
const getServiceOpportunities = async (websiteUrl, serviceString) => {
  const apiCall = async () => {
    const response = await axios.post('https://tp.pressupwards.com/scrape-service-opportunities', {
      url: websiteUrl,
      service: serviceString
    }, {
      headers: {
        'Authorization': 'Bearer your-secret-api-key-12345',
        'Content-Type': 'application/json'
      },
      timeout: 60000
    });

    console.log('Service opportunities API response:', response.data);
    
    // Validate response and return opportunities
    if (response.data && Array.isArray(response.data.opportunities)) {
      return response.data.opportunities;
    }
    
    return [];
  };
  
  try {
    return await executeOpenerWithRetry(apiCall);
  } catch (error) {
    console.error('Service opportunities API call failed after retries:', error.message);
    return []; // Return empty array on failure
  }
};

/**
 * Generates a personalized opener using a simplified 5-line template approach
 * 
 * @param {Object} websiteData - Website data including URL, company name, etc.
 * @param {Object} config - API configuration (apiKey, model, endpoint)
 * @param {Object} openerConfig - Optional opener generation settings
 * @returns {Promise<string>} - AI generated opener
 */
export const generateFiveLineOpener = async (websiteData, config, openerConfig = null) => {
  try {
    // Prepare the context for the AI
    const companyName = formatCompanyName(websiteData.company, websiteData.url);
    const summary = websiteData.summary || 'No company information available';
    const qualificationResult = websiteData.qualificationResult || null;

    // Apply opener configuration if provided
    const style = openerConfig?.style || 'casual';
    const length = openerConfig?.length || 'medium';
    const focus = openerConfig?.focus || 'refresh';
    const serviceType = openerConfig?.serviceType || 'website';
    const subServiceType = openerConfig?.subServiceType || null;
    const manualFocus = openerConfig?.manualFocus || '';

    // Create service-specific context
    let serviceContext = getServiceContext(serviceType, subServiceType, focus, manualFocus);
    
    // Generate the opener using the 5-line template approach
    const opener = await generateTemplateOpener(
      websiteData,
      config,
      serviceContext,
      { style, length },
      qualificationResult
    );
    
    return opener;
  } catch (error) {
    console.error('Error in 5-line opener generation:', error);
    return `[Error generating opener: ${error.message}. Please check your API configuration and try again.]`;
  }
};

/**
 * Creates service-specific context based on service type and focus
 */
const getServiceContext = (serviceType, subServiceType, focus, manualFocus) => {
  let objective = '';
  let triggerType = '';
  let platformName = '';
  
  if (serviceType === 'website') {
    objective = "get a reply to share a one-pager showing website redesign examples";
    triggerType = "website content/offerings that need improvement";
    
    const focusDescriptions = {
      'refresh': 'content freshness and updating website information',
      'financing': '12-month financing options for website redesign',
      'conversion': 'conversion rate optimization and lead generation',
      'mobile': 'mobile responsiveness and user experience',
      'branding': 'brand consistency and messaging',
      'accessibility': 'website accessibility and compliance',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || focusDescriptions.refresh,
      websiteRedesignGuidelines: true
    };
  } 
  else if (serviceType === 'onboarding') {
    platformName = subServiceType === 'apollo' ? 'Apollo.io' : 'Instantly.ai';
    objective = `get a reply to share how ${platformName} can specifically help their business`;
    triggerType = "sales or outreach challenges visible in their content";
    
    const focusDescriptions = subServiceType === 'apollo' ? {
      'prospecting': 'finding better-fit prospects',
      'engagement': 'engaging with prospects more effectively',
      'analytics': 'getting better insights from sales data',
      'integration': 'integrating with existing tools',
      'efficiency': 'automating repetitive prospecting tasks',
      'targeting': 'precise audience targeting',
      'manual': manualFocus
    } : {
      'deliverability': 'improving cold email deliverability',
      'automation': 'automating cold email outreach',
      'scaling': 'scaling cold email outreach efforts',
      'personalization': 'dynamic personalization at scale',
      'analytics': 'detailed email performance tracking',
      'compliance': 'maintaining email regulation compliance',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      subServiceType,
      platformName,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || (subServiceType === 'apollo' ? focusDescriptions.prospecting : focusDescriptions.deliverability),
      apolloGuidelines: subServiceType === 'apollo' ? true : false,
      instantlyGuidelines: subServiceType === 'instantly' ? true : false
    };
  } 
  else if (serviceType === 'cold_email') {
    objective = "get a reply to share a one-pager on cold email systems";
    triggerType = "lead generation challenges visible in their content";
    
    const focusDescriptions = {
      'good_way_to_supplement_leads': 'supplementing existing lead generation',
      'targeting': 'reaching specific audience segments',
      'high_roi': 'measurable ROI for their business model',
      'automation': 'saving time with cold email automation',
      'personalization': 'personalizing outreach at scale',
      'analytics': 'tracking lead performance',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || focusDescriptions.good_way_to_supplement_leads,
      coldEmailGuidelines: true
    };
  }
  else if (serviceType === 'aeo') {
    objective = "get a reply to share insights on AI Engine Optimization (AEO) for their website";
    triggerType = "website content that could be optimized for AI search engines and chatbots";
    
    const focusDescriptions = {
      'ai_visibility': 'improving visibility in AI search results and chatbot recommendations',
      'content_structure': 'structuring content for AI consumption and understanding',
      'semantic_optimization': 'semantic markup and entity optimization for AI engines',
      'voice_search': 'optimizing for voice search and AI assistant responses',
      'ai_snippets': 'optimizing for AI-generated snippets and featured content',
      'chatbot_optimization': 'making content discoverable by AI chatbots',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || focusDescriptions.ai_visibility,
      aeoGuidelines: true
    };
  }
  else if (serviceType === 'partnership') {
    objective = "get a reply to share a case study about a previous partnership";
    triggerType = "agencies or businesses needing web development team support";
    
    const focusDescriptions = {
      'capacity_scaling': 'scaling development capacity without hiring overhead',
      'expertise_access': 'accessing specialized development expertise',
      'cost_efficiency': 'reducing development costs and overhead',
      'quality_delivery': 'maintaining quality while scaling delivery',
      'white_label': 'white-label development services for agencies',
      'overflow_projects': 'handling overflow projects and peak demand',
      'website_management': 'ongoing website management and maintenance services',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || focusDescriptions.capacity_scaling,
      partnershipGuidelines: true
    };
  }
  
  return { serviceType, objective, triggerType, focusArea: 'general improvement' };
};

/**
 * Generate opener using the 5-line template approach
 */
const generateTemplateOpener = async (websiteData, config, serviceContext, styleOptions, qualificationResult = null) => {
  try {
    const { style, length } = styleOptions;
    const companyName = formatCompanyName(websiteData.company, websiteData.url);
    
    // Build service string for API call
    let serviceString = '';
    if (serviceContext.serviceType === 'website') {
      serviceString = 'website redesign';
    } else if (serviceContext.serviceType === 'onboarding') {
      const platform = serviceContext.subServiceType === 'apollo' ? 'Apollo.io' : 'Instantly.ai';
      serviceString = `${platform} onboarding`;
    } else if (serviceContext.serviceType === 'cold_email') {
      serviceString = 'cold email systems';
    } else if (serviceContext.serviceType === 'aeo') {
      serviceString = 'AI Engine Optimization (AEO)';
    } else if (serviceContext.serviceType === 'partnership') {
      serviceString = 'web development partnership';
    }
    
    // Add focus area if available
    if (serviceContext.focusArea) {
      serviceString += ` - ${serviceContext.focusArea}`;
    }
    
    // Get service-specific opportunities for Y component
    const serviceOpportunities = await getServiceOpportunities(websiteData.url, serviceString);
    console.log('Retrieved service opportunities for Y component:', serviceOpportunities);
    
    // Check if we should use specific guidelines based on service type
    const useWebsiteRedesignGuidelines = serviceContext.serviceType === 'website' && serviceContext.websiteRedesignGuidelines;
    const useColdEmailGuidelines = serviceContext.serviceType === 'cold_email' && serviceContext.coldEmailGuidelines;
    const useApolloGuidelines = serviceContext.serviceType === 'onboarding' && serviceContext.subServiceType === 'apollo' && serviceContext.apolloGuidelines;
    const useInstantlyGuidelines = serviceContext.serviceType === 'onboarding' && serviceContext.subServiceType === 'instantly' && serviceContext.instantlyGuidelines;
    const useAeoGuidelines = serviceContext.serviceType === 'aeo' && serviceContext.aeoGuidelines;
    const usePartnershipGuidelines = serviceContext.serviceType === 'partnership' && serviceContext.partnershipGuidelines;
    


    // Create service-specific template prompts
    let templatePrompt;
    
    if (useInstantlyGuidelines) {
      // Use the specific Instantly.ai onboarding guidelines
      templatePrompt = `
        Create a personalized ${style}, ${length} email opener for ${companyName} that follows the SMYKM for Instantly.ai onboarding template structure:

        WEBSITE INFO:
        - Company: ${companyName}
        - Website: ${websiteData.url}
        - Company Summary: ${websiteData.summary || 'No company information available'}
        ${websiteData.talkingPointsData ? `- Existing Opportunities for S component (${websiteData.talkingPointsData.opportunities?.length || 0}): ${websiteData.talkingPointsData.opportunities?.join('; ') || 'No opportunities identified'}` : '- Existing Opportunities for S component: Not available'}
        ${serviceOpportunities.length > 0 ? `- Service-Specific Opportunities for Y component (${serviceOpportunities.length}): ${serviceOpportunities.join('; ')}` : '- Service-Specific Opportunities for Y component: Not available'}
        ${qualificationResult && qualificationResult.success ? `- Prospect Qualification (Fit Score: ${qualificationResult.fit_score || qualificationResult.fitScore}/10): ${qualificationResult.reasoning || 'Analysis completed'}` : ''}
        ${qualificationResult && qualificationResult.keyIndicators && qualificationResult.keyIndicators.length > 0 ? `- Key Success Indicators: ${qualificationResult.keyIndicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.potential_value ? `- Potential Value: ${qualificationResult.potential_value}` : ''}
        ${qualificationResult && qualificationResult.pain_points && qualificationResult.pain_points.length > 0 ? `- Pain Points: ${qualificationResult.pain_points.join('; ')}` : ''}
        ${qualificationResult && qualificationResult.positions && qualificationResult.positions.length > 0 ? `- Positioning Opportunities: ${qualificationResult.positions.join('; ')}` : ''}
        ${websiteData.reasoning ? `- Legacy Fit Score Analysis: ${websiteData.reasoning}` : ''}
        ${websiteData.keyIndicators && websiteData.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${websiteData.keyIndicators.join(', ')}` : ''}
        ${websiteData.potentialValue ? `- Potential Value: ${websiteData.potentialValue}` : ''}
        
        IMPORTANT: For the S (Saw) component, use ONLY the Existing Opportunities listed above. For the Y (You-focused) component, use ONLY the Service-Specific Opportunities listed above. For the M (Mention) component, use the Company Summary above. You can also reference the Fit Score Analysis, Key Success Indicators, and Potential Value to strengthen your opener. DO NOT use any generic assumptions.
        
        SMYKM FOR INSTANTLY.AI ONBOARDING TEMPLATE STRUCTURE (FOLLOW EXACTLY):
        
        S – Saw something: Use ONLY the Existing Opportunities listed above to reference something specific about their company that shows you know them.
         M – Mention something: Connect how the service can help with the focus area related to the opportunity mentioned in the S component (e.g., if S mentions growth challenges, M explains how cold email systems can scale outreach efficiently).
        Y – You-focused hook: Use ONLY the Service-Specific Opportunities listed above to create a compelling benefit that ties directly to their business needs.
        K – Keep it short: 5 lines max, no fluff, 3-7 words per line ideal.
        M – Make the ask: Offer a tiny next step (case study, quick video walkthrough, specific playbook, 3-minute demo, implementation guide).
        
        EXAMPLE FORMAT (FOLLOW THIS STRUCTURE):
        S: Saw your team's growing fast at ${companyName}.
        M: Cold email deliverability probably getting trickier.
        Y: You'd likely see [specific outcome] with [tight solution: domains → warmup → sequences → routing → reporting].
        K: I can share a case study or create a quick video walkthrough for your ICP.
        M: I have a case study ready - should I send it over?
        
        FORMAT REQUIREMENTS:
        - ONLY include these 5 lines in order
        - NO greeting like "Hi" or "Hello" - start directly with the Saw line
        - Each line should be concise and focused
        - No paragraphs, bullets, bold, emojis, exclamation marks
        - No "Hope you're doing well" openers
        - Be conversational but professional
        - Always mention ${companyName} at least once
        - NEVER mention cookies, privacy policies, or any legal policies
        - Make onboarding concrete: domain setup → warmup → sender reputation → sequences → A/B tests → routing → CRM sync → reporting/alerts
        - Promise a tiny artifact first: 60-second Loom or 2-bullet plan for their ICP
        - Anchor to outcomes: meetings booked per rep, safe volume, faster ramp, cleaner handoffs
        - Keep tone human; don't attach files on first touch; ask permission to send
        - Focus on ${serviceContext.focusArea}
        
        WRITE ONLY THE 5 LINES, WITH NO ADDITIONAL TEXT, COMMENTARY OR EXPLANATION.
      `;
    } else if (useApolloGuidelines) {
      // Use the specific Apollo.io onboarding guidelines
      templatePrompt = `
        Create a personalized ${style}, ${length} email opener for ${companyName} that follows the SMYKM for Apollo.io onboarding template structure:

        WEBSITE INFO:
        - Company: ${companyName}
        - Website: ${websiteData.url}
        - Company Summary: ${websiteData.summary || 'No company information available'}
        ${websiteData.talkingPointsData ? `- Existing Opportunities for S component (${websiteData.talkingPointsData.opportunities?.length || 0}): ${websiteData.talkingPointsData.opportunities?.join('; ') || 'No opportunities identified'}` : '- Existing Opportunities for S component: Not available'}
        ${serviceOpportunities.length > 0 ? `- Service-Specific Opportunities for Y component (${serviceOpportunities.length}): ${serviceOpportunities.join('; ')}` : '- Service-Specific Opportunities for Y component: Not available'}
        ${qualificationResult && qualificationResult.success ? `- Prospect Qualification (Fit Score: ${qualificationResult.fit_score || qualificationResult.fitScore}/10): ${qualificationResult.reasoning || 'Analysis completed'}` : ''}
        ${qualificationResult && qualificationResult.key_indicators && qualificationResult.key_indicators.length > 0 ? `- Key Success Indicators: ${qualificationResult.key_indicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.keyIndicators && qualificationResult.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${qualificationResult.keyIndicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.potential_value ? `- Potential Value: ${qualificationResult.potential_value}` : ''}
        ${qualificationResult && qualificationResult.pain_points && qualificationResult.pain_points.length > 0 ? `- Pain Points: ${qualificationResult.pain_points.join('; ')}` : ''}
        ${qualificationResult && qualificationResult.positions && qualificationResult.positions.length > 0 ? `- Positioning Opportunities: ${qualificationResult.positions.join('; ')}` : ''}
        ${websiteData.reasoning ? `- Legacy Fit Score Analysis: ${websiteData.reasoning}` : ''}
        ${websiteData.keyIndicators && websiteData.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${websiteData.keyIndicators.join(', ')}` : ''}
        ${websiteData.potentialValue ? `- Legacy Potential Value: ${websiteData.potentialValue}` : ''}
        
        IMPORTANT: For the S (Saw) component, use ONLY the Existing Opportunities listed above. For the Y (You-focused) component, use ONLY the Service-Specific Opportunities listed above. For the M (Mention) component, use the Company Summary above. You can also reference the Fit Score Analysis, Key Success Indicators, and Potential Value to strengthen your opener. DO NOT use any generic assumptions.
        
        SMYKM FOR APOLLO.IO ONBOARDING TEMPLATE STRUCTURE (FOLLOW EXACTLY):
        
        S – Saw something: Use ONLY the Existing Opportunities listed above to reference something specific about their company that shows you know them.
         M – Mention something: Connect how the service can help with the focus area related to the opportunity mentioned in the S component (e.g., if S mentions lead generation needs, M explains how Apollo.io can provide better prospect data and targeting).
        Y – You-focused hook: Use ONLY the Service-Specific Opportunities listed above to create a compelling benefit that ties directly to their business needs.
        K – Keep it short: 5 lines max, no fluff, 3-7 words per line ideal.
        M – Make the ask: Offer a tiny next step (case study, quick video walkthrough, specific playbook, 3-minute demo, implementation guide).
        
        EXAMPLE FORMAT (FOLLOW THIS STRUCTURE):
        S: Saw your team's scaling at ${companyName}.
        M: Prospecting probably eating up tons of time.
        Y: You'd likely see [specific outcome] with [tight solution: ICP filters → enrichment → sequences → routing].
        K: I can share a case study or create a quick video walkthrough for your ICP.
        M: Send over the case study?
        
        FORMAT REQUIREMENTS:
        - ONLY include these 5 lines in order
        - NO greeting like "Hi" or "Hello" - start directly with the Saw line
        - Each line should be concise and focused
        - No paragraphs, bullets, bold, emojis, exclamation marks
        - No "Hope you're doing well" openers
        - Be conversational but professional
        - Always mention ${companyName} at least once
        - NEVER mention cookies, privacy policies, or any legal policies
        - Make "onboarding" concrete: ICP filters → enrichment → sequences → inbox health → routing → CRM mapping → dashboards
        - Promise a tiny artifact first: 60-second Loom or 2-bullet plan for their ICP
        - Anchor to outcomes they care about: meetings booked per rep, time saved, cleaner CRM
        - Keep tone human; don't attach files on first touch; ask permission to send
        - Focus on ${serviceContext.focusArea}
        
        WRITE ONLY THE 5 LINES, WITH NO ADDITIONAL TEXT, COMMENTARY OR EXPLANATION.
      `;
    } else if (useColdEmailGuidelines) {
      // Use the specific cold email guidelines
      templatePrompt = `
        Create a personalized ${style}, ${length} email opener for ${companyName} that follows the SMYKM for Cold Email Systems template structure:

        WEBSITE INFO:
        - Company: ${companyName}
        - Website: ${websiteData.url}
        - Company Summary: ${websiteData.summary || 'No company information available'}
        ${websiteData.talkingPointsData ? `- Existing Opportunities for S component (${websiteData.talkingPointsData.opportunities?.length || 0}): ${websiteData.talkingPointsData.opportunities?.join('; ') || 'No opportunities identified'}` : '- Existing Opportunities for S component: Not available'}
        ${serviceOpportunities.length > 0 ? `- Service-Specific Opportunities for Y component (${serviceOpportunities.length}): ${serviceOpportunities.join('; ')}` : '- Service-Specific Opportunities for Y component: Not available'}
        ${qualificationResult && qualificationResult.success ? `- Prospect Qualification (Fit Score: ${qualificationResult.fit_score || qualificationResult.fitScore}/10): ${qualificationResult.reasoning || 'Analysis completed'}` : ''}
        ${qualificationResult && qualificationResult.key_indicators && qualificationResult.key_indicators.length > 0 ? `- Key Success Indicators: ${qualificationResult.key_indicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.keyIndicators && qualificationResult.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${qualificationResult.keyIndicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.potential_value ? `- Potential Value: ${qualificationResult.potential_value}` : ''}
        ${qualificationResult && qualificationResult.pain_points && qualificationResult.pain_points.length > 0 ? `- Pain Points: ${qualificationResult.pain_points.join('; ')}` : ''}
        ${qualificationResult && qualificationResult.positions && qualificationResult.positions.length > 0 ? `- Positioning Opportunities: ${qualificationResult.positions.join('; ')}` : ''}
        ${websiteData.reasoning ? `- Legacy Fit Score Analysis: ${websiteData.reasoning}` : ''}
        ${websiteData.keyIndicators && websiteData.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${websiteData.keyIndicators.join(', ')}` : ''}
        ${websiteData.potentialValue ? `- Legacy Potential Value: ${websiteData.potentialValue}` : ''}
        
        IMPORTANT: For the S (Saw) component, use ONLY the Existing Opportunities listed above. For the Y (You-focused) component, use ONLY the Service-Specific Opportunities listed above. For the M (Mention) component, use the Company Summary above. You can also reference the Fit Score Analysis, Key Success Indicators, and Potential Value to strengthen your opener. DO NOT use any generic assumptions.
        
        SMYKM FOR COLD EMAIL SYSTEMS TEMPLATE STRUCTURE (FOLLOW EXACTLY):
        
        S – Saw something: Use ONLY the Existing Opportunities listed above to reference something specific about their company that shows you know them.
        M – Mention something: Connect how the service can help with the focus area related to the opportunity mentioned in the S component (e.g., if S mentions growth challenges, M explains how cold email systems can scale outreach efficiently).
        Y – You-focused hook: Use ONLY the Service-Specific Opportunities listed above to create a compelling benefit that ties directly to their business needs.
        K – Keep it short: 5 lines max, no fluff, 3-7 words per line ideal.
        M – Make the ask: Offer a tiny next step (case study, quick video walkthrough, specific playbook, 3-minute demo, implementation guide).
        
        EXAMPLE FORMAT (FOLLOW THIS STRUCTURE):
        S: Saw your content about building sales processes.
        M: You mentioned challenges with consistent prospecting.
        Y: Cold email system could double qualified leads without adding headcount.
        K: Automated, compliant, and highly personalized.
        M: I made a 60-second demo video - can I send it over?
        
        FORMAT REQUIREMENTS:
        - ONLY include these 5 lines in order
        - NO greeting like "Hi" or "Hello" - start directly with the Saw line
        - Each line should be concise and focused
        - No paragraphs, bullets, bold, emojis, exclamation marks
        - No "Hope you're doing well" openers
        - Be conversational but professional
        - Always mention ${companyName} at least once
        - NEVER mention cookies, privacy policies, or any legal policies
        - Focus on outcomes and benefits, not features
        - Anchor on their stated goals or public moves
        - Name one friction you can remove (manual work, poor targeting, low response rates)
        - Offer a tiny artifact (1-minute Loom, 2 bullets, case study)
        - Focus on ${serviceContext.focusArea}
        
        WRITE ONLY THE 5 LINES, WITH NO ADDITIONAL TEXT, COMMENTARY OR EXPLANATION.
      `;
    } else if (useWebsiteRedesignGuidelines) {
      // Use the specific website redesign guidelines
      templatePrompt = `
        Create a personalized ${style}, ${length} email opener for ${companyName} that follows the SMYKM for web redesign (content- and outcome-based) template structure:

        WEBSITE INFO:
        - Company: ${companyName}
        - Website: ${websiteData.url}
        - Company Summary: ${websiteData.summary || 'No company information available'}
        ${websiteData.talkingPointsData ? `- Existing Opportunities for S component (${websiteData.talkingPointsData.opportunities?.length || 0}): ${websiteData.talkingPointsData.opportunities?.join('; ') || 'No opportunities identified'}` : '- Existing Opportunities for S component: Not available'}
        ${serviceOpportunities.length > 0 ? `- Service-Specific Opportunities for Y component (${serviceOpportunities.length}): ${serviceOpportunities.join('; ')}` : '- Service-Specific Opportunities for Y component: Not available'}
        ${qualificationResult && qualificationResult.success ? `- Prospect Qualification (Fit Score: ${qualificationResult.fit_score || qualificationResult.fitScore}/10): ${qualificationResult.reasoning || 'Analysis completed'}` : ''}
        ${qualificationResult && qualificationResult.key_indicators && qualificationResult.key_indicators.length > 0 ? `- Key Success Indicators: ${qualificationResult.key_indicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.keyIndicators && qualificationResult.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${qualificationResult.keyIndicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.potential_value ? `- Potential Value: ${qualificationResult.potential_value}` : ''}
        ${qualificationResult && qualificationResult.pain_points && qualificationResult.pain_points.length > 0 ? `- Pain Points: ${qualificationResult.pain_points.join('; ')}` : ''}
        ${qualificationResult && qualificationResult.positions && qualificationResult.positions.length > 0 ? `- Positioning Opportunities: ${qualificationResult.positions.join('; ')}` : ''}
        ${websiteData.reasoning ? `- Legacy Fit Score Analysis: ${websiteData.reasoning}` : ''}
        ${websiteData.keyIndicators && websiteData.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${websiteData.keyIndicators.join(', ')}` : ''}
        ${websiteData.potentialValue ? `- Legacy Potential Value: ${websiteData.potentialValue}` : ''}
        
        IMPORTANT: For the S (Saw) component, use ONLY the Existing Opportunities listed above. For the Y (You-focused) component, use ONLY the Service-Specific Opportunities listed above. For the M (Mention) component, use the Company Summary above. You can also reference the Fit Score Analysis, Key Success Indicators, and Potential Value to strengthen your opener. DO NOT use any generic assumptions.
        
        SMYKM FOR WEB REDESIGN TEMPLATE STRUCTURE (FOLLOW EXACTLY):
        
        S – Saw something: Use ONLY the Existing Opportunities listed above to reference something specific about their company that shows you know them.
        M – Mention something: Connect how the service can help with the focus area related to the opportunity mentioned in the S component (e.g., if S mentions website performance issues, M explains how redesign can improve conversion rates).
        Y – You-focused hook: Use ONLY the Service-Specific Opportunities listed above to create a compelling benefit that ties directly to their business needs.
        K – Keep it short: 5 lines max, no fluff.
        M – Make the ask: Offer a tiny next step (case study, quick video walkthrough, specific playbook, 3-minute demo, implementation guide).
        
        NON-VISUAL TRIGGERS TO USE:
        - Messaging/ICP signals: New ICP or segment push, new positioning statement or value prop, category narrative shift.
        - Content/outcome signals: Conversion funnel gaps, unclear CTAs, missing social proof, confusing navigation paths.
        - Business context signals: Funding announcements, team growth, product launches, market expansion, competitive moves.
        - Performance signals: SEO gaps, mobile issues, page speed problems, accessibility concerns.
        
        EXAMPLE FORMAT (FOLLOW THIS STRUCTURE):
        S: Saw your recent funding announcement.
        M: Your homepage messaging might not match your new ICP.
        Y: Quick content refresh could boost trial conversions 15-20%.
        K: Simple copy tweaks, big impact.
        M: I put together a 2-minute walkthrough - want me to share it?
        
        FORMAT REQUIREMENTS:
        - ONLY include these 5 lines in order
        - NO greeting like "Hi" or "Hello" - start directly with the Saw line
        - Each line should be concise and focused
        - No paragraphs, bullets, bold, emojis, exclamation marks
        - No "Hope you're doing well" openers
        - Be conversational but professional
        - Always mention ${companyName} at least once
        - NEVER mention cookies, privacy policies, or any legal policies
        - Focus on content and outcomes, not visual design
        - Anchor on their stated goals or public moves
        - Name one friction you can remove (unclear messaging, poor conversion flow, missing proof)
        - Offer a tiny artifact (1-minute Loom, 2 bullets, case study)
        - Focus on ${serviceContext.focusArea}
        
        WRITE ONLY THE 5 LINES, WITH NO ADDITIONAL TEXT, COMMENTARY OR EXPLANATION.
      `;
    } else if (useAeoGuidelines) {
      // Use the specific AEO (AI Engine Optimization) guidelines
      templatePrompt = `
        Create a personalized ${style}, ${length} email opener for ${companyName} that follows the SMYKM for AI Engine Optimization (AEO) template structure:

        WEBSITE INFO:
        - Company: ${companyName}
        - Website: ${websiteData.url}
        - Company Summary: ${websiteData.summary || 'No company information available'}
        ${websiteData.talkingPointsData ? `- Existing Opportunities for S component (${websiteData.talkingPointsData.opportunities?.length || 0}): ${websiteData.talkingPointsData.opportunities?.join('; ') || 'No opportunities identified'}` : '- Existing Opportunities for S component: Not available'}
        ${serviceOpportunities.length > 0 ? `- Service-Specific Opportunities for Y component (${serviceOpportunities.length}): ${serviceOpportunities.join('; ')}` : '- Service-Specific Opportunities for Y component: Not available'}
        ${qualificationResult && qualificationResult.success ? `- Prospect Qualification (Fit Score: ${qualificationResult.fit_score || qualificationResult.fitScore}/10): ${qualificationResult.reasoning || 'Analysis completed'}` : ''}
        ${qualificationResult && qualificationResult.key_indicators && qualificationResult.key_indicators.length > 0 ? `- Key Success Indicators: ${qualificationResult.key_indicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.keyIndicators && qualificationResult.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${qualificationResult.keyIndicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.potential_value ? `- Potential Value: ${qualificationResult.potential_value}` : ''}
        ${qualificationResult && qualificationResult.pain_points && qualificationResult.pain_points.length > 0 ? `- Pain Points: ${qualificationResult.pain_points.join('; ')}` : ''}
        ${qualificationResult && qualificationResult.positions && qualificationResult.positions.length > 0 ? `- Positioning Opportunities: ${qualificationResult.positions.join('; ')}` : ''}
        ${websiteData.reasoning ? `- Legacy Fit Score Analysis: ${websiteData.reasoning}` : ''}
        ${websiteData.keyIndicators && websiteData.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${websiteData.keyIndicators.join(', ')}` : ''}
        ${websiteData.potentialValue ? `- Legacy Potential Value: ${websiteData.potentialValue}` : ''}
        
        IMPORTANT: For the S (Saw) component, use ONLY the Existing Opportunities listed above. For the Y (You-focused) component, use ONLY the Service-Specific Opportunities listed above. For the M (Mention) component, use the Company Summary above. You can also reference the Fit Score Analysis, Key Success Indicators, and Potential Value to strengthen your opener. DO NOT use any generic assumptions.
        
        SMYKM FOR AI ENGINE OPTIMIZATION (AEO) TEMPLATE STRUCTURE (FOLLOW EXACTLY):
        
        S – Saw something: Use ONLY the Existing Opportunities listed above to reference something specific about their company that shows you know them.
        M – Mention something: Connect how AEO can help with the focus area related to the opportunity mentioned in the S component (e.g., if S mentions content strategy, M explains how AEO can improve AI search visibility).
        Y – You-focused hook: Use ONLY the Service-Specific Opportunities listed above to create a compelling benefit that ties directly to their AI optimization needs.
        K – Keep it short: 5 lines max, no fluff, 3-7 words per line ideal.
        M – Make the ask: Ask if this is the right email to send the report to (creates engagement and confirms contact).
        
        AEO TRIGGERS TO USE:
        - Content structure signals: Unstructured content, missing schema markup, poor semantic organization.
        - AI visibility signals: Low AI search rankings, missing from chatbot responses, poor voice search optimization.
        - Technical signals: Missing structured data, poor entity recognition, inadequate semantic markup.
        - Competitive signals: Competitors ranking better in AI search, better chatbot discovery.
        
        EXAMPLE FORMAT (FOLLOW THIS STRUCTURE):
        S: Saw your content strategy focus at ${companyName}.
        M: AI search engines probably missing your key offerings.
        Y: Your competitors likely ranking higher in ChatGPT responses.
        K: Structured data + semantic markup = better discovery.
        M: Is this the right email to send the report to?
        
        FORMAT REQUIREMENTS:
        - ONLY include these 5 lines in order
        - NO greeting like "Hi" or "Hello" - start directly with the Saw line
        - Each line should be concise and focused
        - No paragraphs, bullets, bold, emojis, exclamation marks
        - No "Hope you're doing well" openers
        - Be conversational but professional
        - Always mention ${companyName} at least once
        - NEVER mention cookies, privacy policies, or any legal policies
        - Focus on AI optimization outcomes: better AI search rankings, improved chatbot discovery, enhanced voice search results
        - Anchor on their content strategy or digital presence
        - Name one AI optimization friction you can remove (poor AI visibility, missing structured data, weak semantic signals)
        - Offer a tiny artifact (AI audit, visibility report, optimization guide)
        - NEVER use generic statistics or percentages (like "40-60% improvement")
        - Make benefits specific and competitive (e.g., "competitors ranking higher in ChatGPT")
        - Focus on ${serviceContext.focusArea}
        
        WRITE ONLY THE 5 LINES, WITH NO ADDITIONAL TEXT, COMMENTARY OR EXPLANATION.
      `;
    } else if (usePartnershipGuidelines) {
      // Use the specific Partnership guidelines
      templatePrompt = `
        Create a personalized ${style}, ${length} email opener for ${companyName} that follows the SMYKM for Web Development Partnership template structure:

        WEBSITE INFO:
        - Company: ${companyName}
        - Website: ${websiteData.url}
        - Company Summary: ${websiteData.summary || 'No company information available'}
        ${websiteData.talkingPointsData ? `- Existing Opportunities for S component (${websiteData.talkingPointsData.opportunities?.length || 0}): ${websiteData.talkingPointsData.opportunities?.join('; ') || 'No opportunities identified'}` : '- Existing Opportunities for S component: Not available'}
        ${serviceOpportunities.length > 0 ? `- Service-Specific Opportunities for Y component (${serviceOpportunities.length}): ${serviceOpportunities.join('; ')}` : '- Service-Specific Opportunities for Y component: Not available'}
        ${qualificationResult && qualificationResult.success ? `- Prospect Qualification (Fit Score: ${qualificationResult.fit_score || qualificationResult.fitScore}/10): ${qualificationResult.reasoning || 'Analysis completed'}` : ''}
        ${qualificationResult && qualificationResult.key_indicators && qualificationResult.key_indicators.length > 0 ? `- Key Success Indicators: ${qualificationResult.key_indicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.keyIndicators && qualificationResult.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${qualificationResult.keyIndicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.potential_value ? `- Potential Value: ${qualificationResult.potential_value}` : ''}
        ${qualificationResult && qualificationResult.pain_points && qualificationResult.pain_points.length > 0 ? `- Pain Points: ${qualificationResult.pain_points.join('; ')}` : ''}
        ${qualificationResult && qualificationResult.positions && qualificationResult.positions.length > 0 ? `- Positioning Opportunities: ${qualificationResult.positions.join('; ')}` : ''}
        ${websiteData.reasoning ? `- Legacy Fit Score Analysis: ${websiteData.reasoning}` : ''}
        ${websiteData.keyIndicators && websiteData.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${websiteData.keyIndicators.join(', ')}` : ''}
        ${websiteData.potentialValue ? `- Legacy Potential Value: ${websiteData.potentialValue}` : ''}
        
        IMPORTANT: For the S (Saw) component, use ONLY the Existing Opportunities listed above. For the Y (You-focused) component, use ONLY the Service-Specific Opportunities listed above. For the M (Mention) component, use the Company Summary above. You can also reference the Fit Score Analysis, Key Success Indicators, and Potential Value to strengthen your opener. DO NOT use any generic assumptions.
        
        SMYKM FOR WEB DEVELOPMENT PARTNERSHIP TEMPLATE STRUCTURE (FOLLOW EXACTLY):
        
        S – Saw something: Use ONLY the Existing Opportunities listed above to reference something specific about their company that shows you know them.
        M – Mention something: Connect how a web development partnership can help with the focus area related to the opportunity mentioned in the S component (e.g., if S mentions scaling challenges, M explains how partnership can provide dedicated development capacity).
        Y – You-focused hook: Use ONLY the Service-Specific Opportunities listed above to create a compelling benefit that ties directly to their partnership needs.
        K – Keep it short: 5 lines max, no fluff, 3-7 words per line ideal.
        M – Make the ask: Ask if this is the right email to send a case study about a previous partnership.
        
        PARTNERSHIP TRIGGERS TO USE:
        - Capacity signals: Growing client demands, scaling challenges, resource constraints.
        - Quality signals: Need for specialized expertise, complex project requirements, technical challenges.
        - Efficiency signals: Tight deadlines, multiple concurrent projects, need for faster delivery.
        - Cost signals: High overhead costs, need for flexible team scaling, budget optimization.
        
        EXAMPLE FORMAT (FOLLOW THIS STRUCTURE):
        S: Saw your agency's rapid growth at ${companyName}.
        M: Scaling development capacity without hiring overhead.
        Y: White-label partnership could handle your overflow projects.
        K: Dedicated team + your brand = seamless client delivery.
        M: Is this the right email to send a case study about a previous partnership?
        
        FORMAT REQUIREMENTS:
        - ONLY include these 5 lines in order
        - NO greeting like "Hi" or "Hello" - start directly with the Saw line
        - Each line should be concise and focused
        - No paragraphs, bullets, bold, emojis, exclamation marks
        - No "Hope you're doing well" openers
        - Be conversational but professional
        - Always mention ${companyName} at least once
        - NEVER mention cookies, privacy policies, or any legal policies
        - Focus on partnership benefits: capacity scaling, expertise access, cost efficiency, quality delivery
        - Anchor on their business growth or client service challenges
        - Name one partnership friction you can remove (capacity constraints, expertise gaps, overhead costs)
        - Offer a case study as the next step to demonstrate proven results
        - Make benefits specific to their business model (agency, consultancy, business)
        - Focus on ${serviceContext.focusArea}
        
        WRITE ONLY THE 5 LINES, WITH NO ADDITIONAL TEXT, COMMENTARY OR EXPLANATION.
      `;
    } else {
      // Fallback generic template
      templatePrompt = `
        Create a personalized ${style}, ${length} email opener for ${companyName} that follows the SMYKM 5-line template structure:

        WEBSITE INFO:
        - Company: ${companyName}
        - Website: ${websiteData.url}
        - Company Summary: ${websiteData.summary || 'No company information available'}
        ${websiteData.talkingPointsData ? `- Existing Opportunities for S component (${websiteData.talkingPointsData.opportunities?.length || 0}): ${websiteData.talkingPointsData.opportunities?.join('; ') || 'No opportunities identified'}` : '- Existing Opportunities for S component: Not available'}
        ${serviceOpportunities.length > 0 ? `- Service-Specific Opportunities for Y component (${serviceOpportunities.length}): ${serviceOpportunities.join('; ')}` : '- Service-Specific Opportunities for Y component: Not available'}
        ${qualificationResult && qualificationResult.success ? `- Prospect Qualification (Fit Score: ${qualificationResult.fit_score || qualificationResult.fitScore}/10): ${qualificationResult.reasoning || 'Analysis completed'}` : ''}
        ${qualificationResult && qualificationResult.key_indicators && qualificationResult.key_indicators.length > 0 ? `- Key Success Indicators: ${qualificationResult.key_indicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.keyIndicators && qualificationResult.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${qualificationResult.keyIndicators.join(', ')}` : ''}
        ${qualificationResult && qualificationResult.potential_value ? `- Potential Value: ${qualificationResult.potential_value}` : ''}
        ${qualificationResult && qualificationResult.pain_points && qualificationResult.pain_points.length > 0 ? `- Pain Points: ${qualificationResult.pain_points.join('; ')}` : ''}
        ${qualificationResult && qualificationResult.positions && qualificationResult.positions.length > 0 ? `- Positioning Opportunities: ${qualificationResult.positions.join('; ')}` : ''}
        ${websiteData.reasoning ? `- Legacy Fit Score Analysis: ${websiteData.reasoning}` : ''}
        ${websiteData.keyIndicators && websiteData.keyIndicators.length > 0 ? `- Legacy Key Success Indicators: ${websiteData.keyIndicators.join(', ')}` : ''}
        ${websiteData.potentialValue ? `- Legacy Potential Value: ${websiteData.potentialValue}` : ''}
        
        IMPORTANT: For the S (Saw) component, use ONLY the Existing Opportunities listed above. For the Y (You-focused) component, use ONLY the Service-Specific Opportunities listed above. For the M (Mention) component, use the Company Summary above. You can also reference the Fit Score Analysis, Key Success Indicators, and Potential Value to strengthen your opener. DO NOT use any generic assumptions.
        
        SMYKM TEMPLATE STRUCTURE (FOLLOW EXACTLY):
        
        S – Saw something: Use specific information from Talking Points Data and Opportunities to reference something about their company that shows you know them and identifies an opportunity where our service can help.
        M – Mention something: Connect how the service can help with the focus area related to the opportunity mentioned in the S component (e.g., if S mentions scaling challenges, M explains how the service addresses that specific need).
        Y – You-focused hook: Connect how your service can help them achieve a specific outcome related to ${serviceContext.focusArea}.
        K – Keep it short: 5 lines total, concise and focused.
        M – Make the ask: Offer a value-driven, low-commitment next step (case study, quick video walkthrough, implementation guide).
        
        FORMAT REQUIREMENTS:
        - ONLY include these 5 lines in order
        - NO greeting like "Hi" or "Hello" - start directly with the Saw line
        - Each line should be concise and focused
        - No paragraphs, bullets, bold, emojis, exclamation marks
        - No "Hope you're doing well" openers
        - Be conversational but professional
        - Always mention ${companyName} at least once
        - NEVER mention cookies, privacy policies, or any legal policies
        - Focus on ${serviceContext.focusArea}
        
        WRITE ONLY THE 5 LINES, WITH NO ADDITIONAL TEXT, COMMENTARY OR EXPLANATION.
      `;
    }

    // Make the AI request
    const response = await makeAIRequest(config, templatePrompt);
    
    if (response.error) {
      throw new Error(`Error generating opener: ${response.error}`);
    }
    
    return processOpenerResponse(response.content, companyName);
  } catch (error) {
    console.error('Error generating template opener:', error);
    throw error;
  }
};



/**
 * Process the opener response to ensure it meets SMYKM template requirements
 */
const processOpenerResponse = (content, companyName) => {
  // Clean up the content first
  let opener = content;
  
  // Remove markdown code blocks if present
  opener = opener.replace(/```json\s*|```\s*$/g, '');
  
  // Remove any explanation text that might be present
  opener = opener.replace(/^(S – Saw something|M – Mention something|Y – You‑focused hook|K – Keep it short|M – Make the ask):/gi, '');
  opener = opener.replace(/^(Saw|Mention|You-focused hook|Keep it short|Make the ask):/gi, '');
  
  // Remove SMYKM prefixes
  opener = opener.replace(/^S:\s*/gim, '');
  opener = opener.replace(/^M:\s*/gim, '');
  opener = opener.replace(/^Y:\s*/gim, '');
  opener = opener.replace(/^K:\s*/gim, '');
  // Second M in SMYKM
  opener = opener.replace(/^M:\s*/gim, '');
  
  // Remove any numbering
  opener = opener.replace(/^\d+\.\s+/gm, '');
  
  // Remove any extra annotations or JSON formatting
  opener = opener.replace(/```json|```|"/g, '');
  opener = opener.replace(/\{|\}/g, '');
  
  // Remove any greeting that might have been added
  const greetings = ['Hey', 'Hi', 'Hello', 'Greetings', 'Dear', 'Hope you are', 'Hope you\'re', 'I hope'];
  for (const greeting of greetings) {
    const regex = new RegExp(`^${greeting}\\s+(?:${companyName})?[,\\s]`, 'i');
    if (opener.match(regex)) {
      opener = opener.replace(regex, '');
      // Capitalize first letter after removing greeting
      opener = opener.charAt(0).toUpperCase() + opener.slice(1);
    }
  }
  
  // Format the opener into 5 distinct lines
  const lines = opener.split(/\n+/).map(line => line.trim()).filter(line => line);
  
  // Ensure we have at least 4 lines (4-5 lines are acceptable)
  if (lines.length < 4) {
    // If we don't have enough lines, throw an error with more context
    console.error('Insufficient content generated:', {
      originalContent: content,
      processedLines: lines,
      lineCount: lines.length,
      companyName
    });
    throw new Error(`AI generated insufficient content for opener. Generated ${lines.length} lines, need at least 4. Please check your API configuration and try again.`);
  }
  
  // Take up to 5 lines (4-5 lines are acceptable)
  const finalLines = lines.slice(0, 5);
  
  // Ensure each line follows the 3-7 words guideline where possible
  const processedLines = finalLines.map(line => {
    // Remove excess spaces
    let processedLine = line.replace(/\s+/g, ' ').trim();
    
    // Ensure line ends with proper punctuation
    if (!processedLine.endsWith('.') && !processedLine.endsWith('?') && !processedLine.endsWith('!')) {
      processedLine += '.';
    }
    
    return processedLine;
  });
  
  // Join the lines with newlines to create the SMYKM format
  return processedLines.join('\n');
};

/**
 * Make an API request to the configured AI provider with retry logic
 */
const makeAIRequestInternal = async (config, prompt, retryCount = 0) => {
  const maxRetries = 5;
  const baseDelay = 2000; // 2 seconds
  
  try {
    const { endpoint, apiKey, model, ollamaEndpoint, useNoThink } = config;
    
    // Add /nothink suffix if option is enabled
    const finalPrompt = useNoThink ? prompt + "/nothink" : prompt;
    
    // Handle different API endpoints
    if (endpoint === 'openai' || endpoint === 'openrouter' || endpoint === 'custom') {
      if (!apiKey || !model) {
        return { error: 'API configuration required. Please add your API key and model in Settings.' };
      }
      
      let apiUrl = '';
      let headers = { 'Content-Type': 'application/json' };
      
      // Configure API endpoint based on provider
      switch (endpoint) {
        case 'openai':
          apiUrl = 'https://api.openai.com/v1/chat/completions';
          headers['Authorization'] = `Bearer ${apiKey}`;
          break;
        case 'openrouter':
          apiUrl = 'https://openrouter.ai/api/v1/chat/completions';
          headers['Authorization'] = `Bearer ${apiKey}`;
          headers['HTTP-Referer'] = window.location.origin;
          headers['X-Title'] = 'Website Analyzer';
          break;
        case 'custom':
          apiUrl = 'https://api.openai.com/v1/chat/completions';
          headers['Authorization'] = `Bearer ${apiKey}`;
          break;
      }
      
      // Make the API request
      const requestBody = {
        model: model,
        messages: [
          { role: 'system', content: 'You are a professional, helpful assistant that creates concise, value-first email openers.' },
          { role: 'user', content: finalPrompt }
        ],
        max_tokens: 300,
        temperature: 0.7
      };
      
      const response = await axios.post(apiUrl, requestBody, { headers });
      
      if (response.data && response.data.choices && response.data.choices[0]) {
        let content = response.data.choices[0].message.content.trim();
        
        // Remove empty <think></think> tags if useNoThink is enabled
        if (useNoThink) {
          content = content.replace(/<think>\s*<\/think>/g, '');
          content = content.replace(/<think><\/think>/g, '');
        }
        
        return { content };
      } else {
        return { error: 'Invalid API response structure' };
      }
    } 
    // For Ollama endpoint
    else if (endpoint === 'ollama') {
      if (!ollamaEndpoint || !model) {
        return { error: 'Ollama configuration required. Please add your endpoint URL and model in Settings.' };
      }
      
      // Make the API request to Ollama
      const requestBody = {
        model: model,
        prompt: finalPrompt,
        stream: false
      };
      
      const response = await axios.post(ollamaEndpoint, requestBody);
      
      if (response.data && response.data.response) {
        let content = response.data.response.trim();
        
        // Remove markdown code blocks if present
        content = content.replace(/```json\s*|```\s*$/g, '');
        
        // Remove empty <think></think> tags if useNoThink is enabled
        if (useNoThink) {
          content = content.replace(/<think>\s*<\/think>/g, '');
          content = content.replace(/<think><\/think>/g, '');
        }
        
        return { content };
      } else {
        return { error: 'Invalid Ollama API response structure' };
      }
    } 
    else {
      return { error: 'Unsupported endpoint type: ' + endpoint };
    }
  } catch (error) {
    console.error('API request error:', error);
    
    // Handle rate limiting (429) and server errors (5xx) with retry
    if (error.response && (error.response.status === 429 || error.response.status >= 500)) {
      if (retryCount < maxRetries) {
        const delay = baseDelay * Math.pow(2, retryCount); // Exponential backoff
        console.log(`Rate limited or server error. Retrying in ${delay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return makeAIRequest(config, prompt, retryCount + 1);
      } else {
        return { error: `Rate limit exceeded. Please wait a moment and try again. (${error.response.status})` };
      }
    }
    
    return { error: error.message || 'Unknown error' };
  }
};

/**
 * Regenerate opener for a specific website
 * @param {Object} website - Website data
 * @param {Object} config - API configuration
 * @param {Object} openerConfig - Optional opener generation settings
 * @returns {Promise<Object>} Result of the operation
 */
export const regenerateFiveLineOpener = async (website, config, openerConfig = null) => {
  try {


    // Create a copy of the website data to ensure we have all required fields
    const websiteData = {
      ...website,
      company: website.company || extractDomain(website.url),
      fitScore: website.fitScore || 5
    };

    // Generate new opener using 5-line template approach
    const newOpener = await generateFiveLineOpener(
      websiteData,
      config,
      openerConfig
    );
    


    // Check if opener contains error message
    if (newOpener.startsWith('[Error') || newOpener.startsWith('[API configuration')) {
      return {
        success: false,
        error: newOpener.replace(/\[|\]/g, '')
      };
    }

    // Note: localStorage update is handled by the calling component to avoid conflicts

    return {
      success: true,
      opener: newOpener,
      serviceType: openerConfig?.serviceType || 'website',
      subServiceType: openerConfig?.subServiceType || null
    };
  } catch (error) {
    console.error('Error regenerating 5-line opener:', error);
    return {
      success: false,
      error: error.message || 'Failed to regenerate opener'
    };
  }
};